import {
  Autocomplete as MuiAutocomplete,
  AutocompleteRenderInputParams,
} from "@mui/material";
import { createFilterOptions } from "@mui/material/useAutocomplete";
import {
  StyledAddIcon,
  StyledAutocomplete,
  StyledInput,
  StyledLoadingIcon,
  StyledPopupIcon,
  StyledSearchIcon,
  StyledValidation,
  useStyles,
} from "Components/Shared/Inputs/Autocomplete/BlAutocompleteStyles";
import { BlTextInput } from "Components/Shared/Inputs/BlTextInput";
import * as React from "react";
import { useFieldError, FieldErrorProps } from "Hooks/Form/useFieldError";
import { StyledInputLabel } from "Components/Shared/StyledComponents";
import { Typography } from "@mui/material";
import useDebounce from "Hooks/useDebounce";
import { usePrevious } from "Hooks/usePrevious";
import { ExpandArrowIcon } from "Components/Shared/Icons";
import { Resources, useResource } from "Translations/Resources";

export type LookupItem = {
  id: number | string;
  name: string;
  data?: string;
};

type Props = FieldErrorProps & {
  options: LookupItem[];
  value?: LookupItem | null;
  allowCustomValue?: boolean;
  onOptionSelected?: (val: LookupItem | null) => void;
  onInputValueChange?: (val: string) => void;
  onAddValue?: (val: string) => void;
  inputName?: string;
  label?: string;
  placeholder?: string;
  isLoading?: boolean;
  debounceTime?: number;
  onSearch?: (searchString: string) => void;
  onBlur?: React.FocusEventHandler;
  disabled?: boolean;
};

const filterOptions = createFilterOptions({
  matchFrom: "any",
  stringify: (option: LookupItem) => `${option.name} + ${option.data}`,
});

const formatOption = (option: LookupItem) => {
  return `${option.name}`;
};

const popupIcon = (allowCustomValue: boolean) => {
  return (
    <StyledPopupIcon>
      <ExpandArrowIcon />
    </StyledPopupIcon>
  );
};

const closeIcon = () => {
  return <StyledPopupIcon></StyledPopupIcon>;
};

export const BlAutocomplete: React.FunctionComponent<Props> = props => {
  const { hasError, errorMessage, originalProps } = useFieldError(props);
  const {
    onInputValueChange,
    onOptionSelected,
    onAddValue,
    allowCustomValue,
    inputName,
    placeholder,
    label,
    debounceTime,
    onSearch,
    onBlur,
    disabled,
  } = originalProps;

  const { t } = useResource();

  const [autocompleteValue, setAutocompleteValue] =
    React.useState<LookupItem | null>(null);
  const [highlightedValue, setHighlightedValue] =
    React.useState<LookupItem | null>(null);
  const [inputValue, setInputValue] = React.useState<string>("");
  const [isOpen, setIsOpen] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(props.isLoading);
  const [options, setOptions] = React.useState(props.options);
  const classes = useStyles(hasError)();

  const [searchTerm, setSearchTerm] = React.useState("");
  const prevSearchTerm = usePrevious(searchTerm);
  const [lastSearchTerm, setLastSearchTerm] = React.useState("");
  const debouncedSearchTerm = useDebounce(searchTerm, debounceTime ?? 500);

  React.useEffect(() => {
    if (debouncedSearchTerm !== lastSearchTerm && onSearch) {
      onSearch(debouncedSearchTerm);
      setLastSearchTerm(debouncedSearchTerm);
    }
  }, [debouncedSearchTerm, onSearch, lastSearchTerm]);

  React.useEffect(() => {
    setIsLoading(props.isLoading);
  }, [props.isLoading]);

  React.useEffect(() => {
    setOptions(props.options);
  }, [props.options]);

  React.useEffect(() => {
    setAutocompleteValue(v =>
      v?.id === originalProps.value?.id ? v : originalProps.value ?? null,
    );
  }, [originalProps.value]);

  const onChange = (
    _: React.ChangeEvent<{}>,
    val: LookupItem | string | null,
  ) => {
    setAutocompleteValue(val as LookupItem);
    onOptionSelected && onOptionSelected(val as LookupItem);
    setIsLoading(false);
    setHighlightedValue(null);
  };

  const onInputChange = (_: React.ChangeEvent<{}>, val: string | null) => {
    setInputValue(val ?? "");
    onInputValueChange && onInputValueChange(val ?? "");
    if (onSearch) {
      setSearchTerm((val as string) ?? "");

      if (prevSearchTerm && prevSearchTerm !== val) {
        setOptions([]);
      }
    }
  };

  const handleAddValue = () => {
    onAddValue && onAddValue(inputValue);
    setInputValue("");
    setAutocompleteValue(null);
  };

  const getInput = (params: AutocompleteRenderInputParams) => {
    return (
      <StyledInput>
        <BlTextInput
          {...params}
          name={inputName}
          placeholder={placeholder}
          disabled={disabled}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {isLoading && isOpen && <StyledLoadingIcon size={25} />}
                {params.InputProps.endAdornment}
              </>
            ),
            startAdornment: <StyledSearchIcon />,
          }}
        />
        {allowCustomValue && (
          <StyledAddIcon onClick={handleAddValue}></StyledAddIcon>
        )}
      </StyledInput>
    );
  };

  return (
    <>
      {label && (
        <StyledInputLabel htmlFor={inputName} $hasError={hasError}>
          <Typography>{label}</Typography>
        </StyledInputLabel>
      )}
      <StyledAutocomplete
        $allowCustomValue={!!allowCustomValue}
        $open={isOpen}
        $hasError={hasError}
      >
        <MuiAutocomplete
          value={autocompleteValue}
          inputValue={inputValue}
          options={options}
          onChange={onChange}
          onInputChange={onInputChange}
          renderInput={params => getInput(params)}
          filterOptions={filterOptions}
          forcePopupIcon={true}
          getOptionLabel={option => formatOption(option)}
          getOptionKey={option => option.id.toString()}
          isOptionEqualToValue={(option, val) => option.id === val.id}
          popupIcon={popupIcon(!!allowCustomValue)}
          clearIcon={allowCustomValue || isLoading ? null : closeIcon()}
          disabled={disabled}
          classes={{
            popper: classes.popper,
          }}
          onOpen={() => {
            setIsOpen(true);
            if (onSearch) {
              onSearch(debouncedSearchTerm);
            }
          }}
          onClose={() => {
            setIsOpen(false);
            setSearchTerm("");
          }}
          autoHighlight
          clearText={t(Resources.Autocomplete.ClearText)}
          closeText={t(Resources.Autocomplete.CloseText)}
          openText={t(Resources.Autocomplete.OpenText)}
          noOptionsText={t(Resources.Autocomplete.NoOptionsText)}
          loadingText={t(Resources.Autocomplete.LoadingText)}
          loading={false}
          onKeyDown={e => {
            if (e.key === "Enter") {
              e.preventDefault();
              e.stopPropagation();
              !highlightedValue && handleAddValue();
            }
          }}
          onHighlightChange={(_, val: LookupItem | null) => {
            setHighlightedValue(val);
          }}
          onBlur={onBlur}
        />
        {hasError && <StyledValidation>{errorMessage}</StyledValidation>}
      </StyledAutocomplete>
    </>
  );
};
